import React, { useMemo } from "react";
import { FormSection, Field } from "redux-form";

import  {
	Card,
	ProgressIndicator,
	ReduxControls
} from "rhombus-react";

import { SOCIAL_AD_TYPES } from "utils/constants";
import { useErrorBanner } from "social/utils";
import { isDynamic } from "social/DynamicCreativeToggle";
import { assetType } from "social/BuildMethodRadio";
import DynamicInput from "social/DynamicInput";

import CardContent from "social/carousel/CardContent";

import LinkedInCarouselCardForm, { LINKEDIN_CAROUSEL_FIELDS } from "social/carousel/LinkedInCarouselCardForm";
import { CREATIVE_NAME_MAX_LENGTH } from "widgets/forms/validators/Creatives";
import { LINKEDIN_CAROUSEL_CONSTRAINTS } from "social/validators/linkedIn";
import { UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE } from "social/constants/common";

const {
	MAX_LENGTH_INTRO_DESCRIPTION,
	MAX_LENGTH_LANDING_PAGE,
} = LINKEDIN_CAROUSEL_CONSTRAINTS;

export const LINKEDIN_CAROUSEL_MAIN_FIELDS = {
	creativeName: {
		name: "name",
		label: "Creative Name",
		className: "full-width cypress-creative-name",
		placeholder: "Enter a creative name",
		component: ReduxControls.Input,
		charMax: CREATIVE_NAME_MAX_LENGTH,
		showCharCount: true,
		showPristineErrors: true,
		required: true,
	},
	introDescription: {
		name: "introDescription",
		label: "Intro/Description",
		className: "full-width cypress-creative-name",
		placeholder: "Enter description",
		component: DynamicInput,
		defaultDynVarName: "copy",
		charMax: MAX_LENGTH_INTRO_DESCRIPTION,
		// showCharCount
		required: true,
	},
	landingPage: {
		name: "landingPage",
		label: "Landing Page",
		className: "full-width cypress-creative-landing-page",
		placeholder: "https://www.example.com",
		component: DynamicInput,
		defaultDynVarName: "clickTag1",
		charMax: MAX_LENGTH_LANDING_PAGE,
		// showCharCount
		required: true,
	},
};

function fieldGenerator(props) {
	const {
		showPristineErrors,
		isViewOnly,
		doVersionsExist,
	} = props;

	return (name) => {
		const fieldProps = LINKEDIN_CAROUSEL_MAIN_FIELDS[name];

		let extraProps = {
			showPristineErrors,
			disabled: isViewOnly,
		};

		if(extraProps.disabled && fieldProps.placeholder){
			// We set this on extraProps, because we don't want to mutate the fieldProps consts
			extraProps.placeholder = "";
		}

		// If versions exist besides default, then dynamic toggle should be disabled
		if (doVersionsExist) {
			extraProps.dynamicToggleDisabled = true;
			extraProps.variableNameInputDisabled = true;
			extraProps.variableNameInputTip = UNEDITABLE_DYNAMIC_VARIABLE_MESSAGE;
		}

		return (
			<Field
				key={fieldProps.name}
				{...fieldProps}
				{...extraProps}
			/>
		);
	};
}

export default function LinkedInCarousel(props){
	const {
		libraryId,
		navigationActions,
		isViewOnly,
		showPristineErrors,
		doVersionsExist,
	} = props;
	const adType = SOCIAL_AD_TYPES.LINKEDIN_CAROUSEL;

	const showPristine = showPristineErrors || ["cardCount"]; // Always show at least the card count error

	const errorDisplay = useErrorBanner(adType, showPristine, LINKEDIN_CAROUSEL_FIELDS);

	const fields = useMemo(() => {
		const generateFields = fieldGenerator({
			isViewOnly,
			showPristineErrors,
			doVersionsExist,
		});

		return Object.keys(LINKEDIN_CAROUSEL_MAIN_FIELDS).map(generateFields);
	}, [isViewOnly, showPristineErrors, doVersionsExist]);

	return (
		<FormSection name={adType}>
			<div className="carousel-modal-content progress-container">
				{errorDisplay}
				<ProgressIndicator isLoading={false} />
				<div className="form-container">
					<div className="fields">
						<Field {...isDynamic} />
						<Field {...assetType} />
					</div>
				</div>
				<Card
					className="carousel-main-content"
					header="Main Content"
					isCollapsable
					isVisible
				>
					<div className="form-container">
						{fields}
					</div>
				</Card>
				<CardContent
					libraryId={libraryId}
					adType={adType}
					isViewOnly={isViewOnly}
					showPristineErrors={showPristineErrors}
					navigationActions={navigationActions}
					CardForm={LinkedInCarouselCardForm}
					doVersionsExist={doVersionsExist}
				/>
			</div>
		</FormSection>
	);
}

LinkedInCarousel.displayName = "LinkedInCarousel";
